import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavbarService } from '../../services/navbar.service';
import { SessionService } from '../../services/session.service';
import { NotificationsService } from '../../services/notifications.service';
import { filter, Subscription } from 'rxjs';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { NotificationsComponent } from '../notifications/notifications';

interface NavItem {
  item: number;
  name: string;
  active: boolean;
  route: string;
}

@Component({
  selector: 'app-navbar',
  imports: [CommonModule, RouterModule, NotificationsComponent],
  templateUrl: './navbar.html',
  styleUrl: './navbar.css'
})
export class Navbar implements OnInit, OnDestroy {
  // Items principales que siempre están visibles
  public mainNavItems: NavItem[] = [
    { item: 0, name: "Inicio", active: false, route: '/home-page' },
    { item: 1, name: "Prospectos", active: false, route: '/prospects' },
    { item: 2, name: "Cuentas", active: false, route: '/accounts' },
    { item: 3, name: "Contactos", active: false, route: '/contacts' },
    { item: 4, name: "Oportunidades", active: false, route: '/opportunities' },
    { item: 5, name: "Presupuestos", active: false, route: '/budget' },
    { item: 6, name: "Campañas", active: false, route: '/campaigns' },
    { item: 7, name: "Metas", active: false, route: '/goals' }
  ];

  // Items que están en el menú "Más"
  public moreNavItems: NavItem[] = [
    { item: 9, name: "Tareas", active: false, route: '/tasks' },
    { item: 8, name: "Configuración", active: false, route: '/user-table' },
    { item: 10, name: "Forecast", active: false, route: '/forecast' },
    { item: 11, name: "Calendario", active: false, route: '/calendar' },
    { item: 12, name: "Contratos", active: false, route: '/contracts' }
  ];

  // Todos los items combinados para facilitar la búsqueda
  public get allNavItems(): NavItem[] {
    return [...this.mainNavItems, ...this.moreNavItems];
  }

  public showNavbar: boolean = true;
  public activeUserName: string = '';
  public showNotifications: boolean = false;
  public unreadNotificationsCount: number = 0;
  public showMoreDropdown: boolean = false;
  public dropdownPosition = { top: '60px', left: 'auto', right: '20px' };
  private subscriptions: Subscription = new Subscription();

  constructor(
    private navbarService: NavbarService,
    private router: Router,
    private sessionService: SessionService,
    private notificationsService: NotificationsService
  ) {}

  ngOnInit(): void {
    const decoded = this.sessionService.decodeToken();
const roleName = (decoded?.roleName || '').toLowerCase();
const isAdmin = roleName === 'admin' || roleName === 'administrador';

if (!isAdmin) {
  this.moreNavItems = this.moreNavItems.filter(item => item.route !== '/forecast');
}



    this.subscriptions.add(
      this.navbarService.showNavbar.subscribe((value) => {
        this.showNavbar = value;
      })
    );

    this.subscriptions.add(
      this.sessionService.userName$.subscribe(name => {
        this.activeUserName = name;
      })
    );

    // Suscribirse al contador de notificaciones no leídas
    this.subscriptions.add(
      this.notificationsService.unreadCount$.subscribe(count => {
        this.unreadNotificationsCount = count;
      })
    );

    const initialName = this.sessionService.getUserName();
    if (initialName) {
      this.activeUserName = initialName;
    } else {
      this.sessionService.fetchUserData();
    }

    this.subscriptions.add(
      this.router.events
        .pipe(filter(event => event instanceof NavigationEnd))
        .subscribe(() => {
          this.updateActiveTab(this.router.url);
        })
    );

    this.updateActiveTab(this.router.url);

    // Agregar listener para cerrar dropdown al hacer scroll
    this.addScrollListener();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.removeScrollListener();
  }

  updateActiveTab(url: string): void {
    // Resetear todos los items
    this.allNavItems.forEach(item => item.active = false);

    // Encontrar el item activo por ruta
    const activeItem = this.allNavItems.find(item => {
      if (item.route === '/home-page' && (url === '/' || url.startsWith('/home-page'))) return true;
      if (item.route === '/prospects' && (url.startsWith('/prospect') || url.startsWith('/detalle'))) return true;
      if (item.route === '/accounts' && url.startsWith('/account')) return true;
      if (item.route === '/contacts' && url.startsWith('/contact')) return true;
      if (item.route === '/opportunities' && url.startsWith('/opportunities')) return true;
      if (item.route === '/budget' && url.startsWith('/budget')) return true;
      if (item.route === '/campaigns' && url.startsWith('/campaign')) return true;
      if (item.route === '/goals' && url.startsWith('/goals')) return true;
      if (item.route === '/user-table' && url.startsWith('/user-table')) return true;
      if (item.route === '/tasks' && url.startsWith('/tasks')) return true;
      if (item.route === '/forecast' && url.startsWith('/forecast')) return true;
      if (item.route === '/calendar' && url.startsWith('/calendar')) return true;
      if (item.route === '/contracts' && url.startsWith('/contracts')) return true;
      return false;
    });

    if (activeItem) {
      activeItem.active = true;
      
      // Si el item activo está en moreNavItems, intercambiarlo con el último de mainNavItems
      if (this.moreNavItems.includes(activeItem)) {
        this.moveItemToMain(activeItem);
      }
    }
  }

  logout(): void {
    // Detener el polling de notificaciones antes del logout
    this.notificationsService.stopPolling();
    
    // Limpiar los datos de sesión
    this.sessionService.clearUserData();
    
    // Navegar al login
    this.router.navigate(['/login']);
  }

  toggleNotifications(): void {
    this.showNotifications = !this.showNotifications;
  }

  closeNotifications(): void {
    this.showNotifications = false;
  }

  onCreateNewGoal(): void {
    // Navegar a la página de creación de metas
    this.router.navigate(['/goals']);
    this.closeNotifications();
  }

  // Métodos para el menú "Más"
  toggleMoreDropdown(event?: Event): void {
    this.showMoreDropdown = !this.showMoreDropdown;
    
    if (this.showMoreDropdown && event) {
      // Calcular posición del dropdown basado en el elemento clickeado
      const target = event.target as HTMLElement;
      const rect = target.getBoundingClientRect();
      
      this.dropdownPosition = {
        top: `${rect.bottom + 2}px`,
        left: 'auto',
        right: `${window.innerWidth - rect.right}px`
      };
    }
  }

  closeMoreDropdown(): void {
    this.showMoreDropdown = false;
  }

  // Mover item del menú "Más" a la barra principal
  moveItemToMain(selectedItem: NavItem): void {
    // Encontrar el índice del item en moreNavItems
    const moreIndex = this.moreNavItems.findIndex(item => item.item === selectedItem.item);
    if (moreIndex === -1) return;

    // Remover el item de moreNavItems
    this.moreNavItems.splice(moreIndex, 1);

    // Mover el último item de mainNavItems a moreNavItems (excepto los primeros 8 que incluyen Metas como fijo)
    if (this.mainNavItems.length > 8) {
      const lastMainItem = this.mainNavItems.pop();
      if (lastMainItem) {
        this.moreNavItems.unshift(lastMainItem);
      }
    }

    // Agregar el item seleccionado al final de mainNavItems
    this.mainNavItems.push(selectedItem);
    
    // Cerrar el dropdown
    this.closeMoreDropdown();
  }

  // Navegar a una opción del menú "Más"
  navigateToMoreItem(item: NavItem): void {
    this.router.navigate([item.route]);
    this.moveItemToMain(item);
  }

  // Obtener el item activo del menú "Más" para mostrar en el botón
  get activeMoreItem(): NavItem | null {
    return this.moreNavItems.find(item => item.active) || null;
  }

  // Métodos para manejar el scroll
  private scrollListener = () => {
    if (this.showMoreDropdown) {
      this.closeMoreDropdown();
    }
  };

  private addScrollListener(): void {
    if (typeof window !== 'undefined') {
      window.addEventListener('scroll', this.scrollListener, { passive: true });
    }
  }

  private removeScrollListener(): void {
    if (typeof window !== 'undefined') {
      window.removeEventListener('scroll', this.scrollListener);
    }
  }
}